import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import ModuleEditorBase from './ModuleEditorBase';
import { PENDING_PLACEHOLDER } from './contentPlaceholder';

interface CodeFileEditorProps {
  module: {
    id: string;
    title: string;
    type: string;
    details: {
      language?: string;
      code?: string;
      filename?: string;
      description?: string;
      file_url?: string;
    };
  };
  sectionId: string;
  courseId: string;
  onUpdate: (moduleId: string, updates: any) => Promise<void>;
  onDelete: (moduleId: string) => Promise<void>;
}

const LANGUAGES = [
  'python', 'javascript', 'typescript', 'java', 'cpp', 'csharp',
  'go', 'rust', 'php', 'ruby', 'swift', 'kotlin', 'html', 'css', 'sql'
];

export default function CodeFileEditor({ module, onUpdate, onDelete }: CodeFileEditorProps) {
  const [language, setLanguage] = useState(module.details.language || 'python');
  const [code, setCode] = useState(module.details.code || '');
  const [description, setDescription] = useState(module.details.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-save with debounce using useEffect
  useEffect(() => {
    const sanitizedDescription = DOMPurify.sanitize(description, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });

    if (language !== module.details.language ||
        code !== module.details.code ||
        sanitizedDescription !== (module.details.description || '')) {
      const timeout = setTimeout(async () => {
        try {
          setIsSaving(true);
          setIsSaved(false);
          await onUpdate(module.id, {
            details: {
              language,
              code,
              description: sanitizedDescription,
              filename: module.details.filename,
              // Backend source_code_file validator requires a `file_url` string
              // of ≥ 1 char. This editor stores the code inline, so we preserve
              // whatever the seed placed here (or the sentinel) rather than
              // losing the field on autosave.
              file_url: module.details.file_url || PENDING_PLACEHOLDER,
            },
          });
          setIsSaved(true);
          setTimeout(() => setIsSaved(false), 2000);
        } catch (error) {
          console.error('Failed to save code:', error);
        } finally {
          setIsSaving(false);
        }
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [language, code, description, module.id, module.details.language, module.details.code, module.details.description, module.details.filename, onUpdate]);

  const handleFileUpload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    try {
      const text = await file.text();
      const ext = file.name.split('.').pop()?.toLowerCase();
      const langMap: Record<string, string> = {
        py: 'python', js: 'javascript', ts: 'typescript', java: 'java',
        cpp: 'cpp', cs: 'csharp', go: 'go', rs: 'rust', php: 'php',
        rb: 'ruby', swift: 'swift', kt: 'kotlin', html: 'html', css: 'css', sql: 'sql'
      };
      const detectedLang = langMap[ext || ''] || language;

      setCode(text);
      setLanguage(detectedLang);
      await onUpdate(module.id, {
        details: {
          language: detectedLang,
          code: text,
          filename: file.name,
          description,
          file_url: module.details.file_url || PENDING_PLACEHOLDER,
        },
      });
    } catch (error) {
      console.error('Failed to read file:', error);
      alert('Failed to read file');
    }
  };

  return (
    <ModuleEditorBase
      moduleType={module.type}
      moduleTitle={module.title}
      onTitleChange={async (title) => await onUpdate(module.id, { title })}
      onDelete={async () => await onDelete(module.id)}
      isSaving={isSaving}
      isSaved={isSaved}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Language <span className="text-error-500">*</span>
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-surface-primary text-text-primary"
          >
            {LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang.charAt(0).toUpperCase() + lang.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Code Content <span className="text-error-500">*</span>
          </label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste code here..."
            rows={15}
            maxLength={100000}
            className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-surface-primary text-text-primary font-mono text-sm"
          />
          <p className="mt-1 text-xs text-text-secondary">
            {code.length.toLocaleString()}/100,000 characters
          </p>
        </div>

        <div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 text-sm font-medium text-text-secondary bg-surface-primary border border-border-primary rounded-full hover:bg-grey-50 dark:hover:bg-grey-800"
          >
            📁 Upload File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".py,.js,.ts,.java,.cpp,.c,.cs,.go,.rs,.php,.rb,.swift,.kt,.html,.css,.sql"
            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
            className="hidden"
          />
          <p className="mt-1 text-xs text-text-secondary">
            or paste code above (max 5MB)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Description (optional)
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Example: Hello World function"
            maxLength={500}
            className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-surface-primary text-text-primary"
          />
        </div>
      </div>
    </ModuleEditorBase>
  );
}
